import React, { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import {
  BLOG_INDEX,
  getBlogPostBySlug,
  getRelatedBlogPosts,
} from '../data/blogPosts';
import '../styles/Blog.css';

const formatDate = (iso) => {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const BlogSectionBlock = ({ section }) => (
  <section>
    {section.heading ? <h2>{section.heading}</h2> : null}
    {(section.paragraphs || []).map((paragraph) => (
      <p key={paragraph.slice(0, 48)}>{paragraph}</p>
    ))}
    {section.list ? (
      <ul>
        {section.list.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    ) : null}
    {section.orderedList ? (
      <ol>
        {section.orderedList.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ol>
    ) : null}
    {(section.subsections || []).map((sub) => (
      <div key={sub.heading || (sub.paragraphs || [])[0]}>
        {sub.heading ? <h3>{sub.heading}</h3> : null}
        {(sub.paragraphs || []).map((paragraph) => (
          <p key={paragraph.slice(0, 64)}>{paragraph}</p>
        ))}
        {sub.list ? (
          <ul>
            {sub.list.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : null}
      </div>
    ))}
    {(section.faqs || []).map((item) => (
      <div key={item.question}>
        <h3>{item.question}</h3>
        <p>{item.answer}</p>
      </div>
    ))}
    {section.links?.length ? (
      <div className="blog-cta-box">
        <div className="blog-cta-actions">
          {section.links.map((link) => (
            <Link key={link.linkText} to={link.linkTo}>
              {link.linkText}
            </Link>
          ))}
        </div>
      </div>
    ) : null}
  </section>
);

const BlogPost = () => {
  const { slug } = useParams();
  const post = getBlogPostBySlug(slug);
  const related = useMemo(() => (post ? getRelatedBlogPosts(post.slug, 5) : []), [post]);

  const jsonLd = useMemo(() => {
    if (!post) return [];
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.heading,
        description: post.description,
        datePublished: post.publishedAt,
        dateModified: post.updatedAt,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://easydroptaxi.com${post.path}`,
        },
        author: {
          '@type': 'Organization',
          name: 'Easy Drop Taxi',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Easy Drop Taxi',
          url: 'https://easydroptaxi.com/',
        },
        keywords: post.tags.join(', '),
      },
      ...((post.sections || []).flatMap((section) => section.faqs || []).length
        ? [{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: post.sections.flatMap((section) => section.faqs || []).map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }]
        : []),
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://easydroptaxi.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Blog',
            item: `https://easydroptaxi.com${BLOG_INDEX.path}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: post.heading,
            item: `https://easydroptaxi.com${post.path}`,
          },
        ],
      },
    ];
  }, [post]);

  if (!post) {
    return <Navigate to={BLOG_INDEX.path} replace />;
  }

  return (
    <main className="blog-page">
      <SeoHead
        title={post.title}
        description={post.description}
        path={post.path}
        jsonLd={jsonLd}
      />

      <section
        className="page-banner block-space text-white"
        style={{ backgroundColor: 'var(--secondary-color)' }}
      >
        <div className="container">
          <nav className="blog-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span> / </span>
            <Link to={BLOG_INDEX.path}>Blog</Link>
            <span> / Article</span>
          </nav>
          <h1 className="display-4 fw-bold">{post.heading}</h1>
          <p className="lead mb-0">{post.description}</p>
        </div>
      </section>

      <section className="block-space">
        <div className="container">
          <div className="blog-layout">
            <article className="blog-article">
              <p className="blog-article-meta">
                Updated {formatDate(post.updatedAt)}
              </p>

              {post.sections.map((section, index) => (
                <BlogSectionBlock
                  key={`${post.slug}-${section.heading || 'block'}-${index}`}
                  section={section}
                />
              ))}

              {post.cta ? (
                <div className="blog-cta-box">
                  {post.cta.text ? <p>{post.cta.text}</p> : null}
                  {post.cta.links?.length ? (
                    <div className="blog-cta-actions">
                      {post.cta.links.map((link) => (
                        <Link key={link.linkText} to={link.linkTo}>
                          {link.linkText}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                  {post.cta.linkTo ? (
                    <Link to={post.cta.linkTo}>
                      {post.cta.linkText || 'Learn more'}
                    </Link>
                  ) : null}
                </div>
              ) : null}
              {post.disclaimer ? <p className="blog-disclaimer">{post.disclaimer}</p> : null}

              {post.tags?.length ? (
                <div className="blog-tags" aria-label="Topics">
                  {post.tags.map((tag) => (
                    <span key={tag} className="blog-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}
            </article>

            <aside className="blog-sidebar" aria-label="Related articles">
              <h2>Related guides</h2>
              <ul className="blog-sidebar-list">
                {related.map((item) => (
                  <li key={item.slug}>
                    <Link to={item.path}>{item.heading}</Link>
                  </li>
                ))}
              </ul>
              {post.relatedLinks?.length ? (
                <>
                  <h2>City hubs &amp; routes</h2>
                  <ul className="blog-sidebar-list">
                    {post.relatedLinks.map((link) => (
                      <li key={link.to}>
                        <Link to={link.to}>{link.label}</Link>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              <h2>Explore</h2>
              <ul className="blog-sidebar-list">
                <li>
                  <Link to={BLOG_INDEX.path}>All blog articles</Link>
                </li>
                <li>
                  <Link to="/taxi-routes">All taxi routes</Link>
                </li>
                <li>
                  <Link to="/tariff">Tariff &amp; terms</Link>
                </li>
                <li>
                  <Link to="/contact">Contact Easy Drop Taxi</Link>
                </li>
              </ul>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BlogPost;
